import { useCallback, useEffect, useState } from 'react';
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '@modules/content-plan/utils/stripUndefined';
import type { PlatformDesignProfile } from '@modules/content-plan/types';
import type { AuthorBasePath } from '@modules/content-plan/services/AuthorPlatformService';

export interface UsePlatformDesignReturn {
  design: PlatformDesignProfile | null;
  loading: boolean;
  error: Error | null;
  save: (patch: Partial<PlatformDesignProfile>) => Promise<void>;
}

export function usePlatformDesign(
  projectId: string | null | undefined,
  authorId: string | null | undefined,
  platformId: string | null | undefined,
  basePath: AuthorBasePath = 'authors',
  profileKey = 'design',
): UsePlatformDesignReturn {
  const [design, setDesign] = useState<PlatformDesignProfile | null>(null);
  const [loading, setLoading] = useState(Boolean(projectId && authorId && platformId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !authorId || !platformId) {
      setDesign(null);
      setLoading(false);
      return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const safeProfileKey = normalizeProfileKey(profileKey);
    const ref = doc(
      db,
      'projects',
      projectId,
      basePath,
      authorId,
      'platforms',
      platformId,
      'design',
      safeProfileKey,
    );
    setLoading(true);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setDesign(snap.exists()
          ? ({ ...(snap.data() as PlatformDesignProfile), authorId, platformId })
          : null);
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, authorId, platformId, basePath, profileKey]);

  const save = useCallback(async (patch: Partial<PlatformDesignProfile>) => {
    if (!projectId || !authorId || !platformId) return;
    const db = CoreFirebaseService.getInstance().firestore;
    const safeProfileKey = normalizeProfileKey(profileKey);
    const ref = doc(
      db,
      'projects',
      projectId,
      basePath,
      authorId,
      'platforms',
      platformId,
      'design',
      safeProfileKey,
    );
    const stripped = stripUndefined({
      ...patch,
      authorId,
      platformId,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    await setDoc(ref, stripped, { merge: true });
  }, [projectId, authorId, platformId, basePath, profileKey]);

  return { design, loading, error, save };
}

function normalizeProfileKey(value: string): string {
  const trimmed = String(value || 'design').trim();
  return /^[a-z0-9][a-z0-9_-]*$/.test(trimmed) ? trimmed : 'design';
}
